import type { CiTestResult, DumpPhaseBreakdown, RestorePodTiming } from "@/lib/types";
import { getPhaseTooltip } from "./phase-tooltips";
import { msFromSeconds, type Phase, WaterfallShell } from "./waterfall-core";

/**
 * Build the nested sub-phases under "CRIU Dump" from the agent-supplied
 * dump_phases breakdown. The full criu_dump_ms aggregate is always rendered
 * as the parent bar; the children are positioned cascading from t=0 inside
 * the parent so the bar lengths reflect actual sub-phase durations.
 *
 * Returns an empty list if every key is zero/missing — the caller falls
 * back to a flat "CRIU Dump" bar without sub-bars.
 */
function buildDumpSubPhases(dumpPhases: DumpPhaseBreakdown): Phase[] {
  // Order matches the source-side agent slog markers: starting checkpoint →
  // engine ready → running CRIU dump → calling wake (post-dump) → checkpoint
  // complete. Colors group related work — slate for the cold-start wait so it
  // visually contrasts with the actual CRIU work below.
  const subPhaseSpecs: [string, number | undefined, string][] = [
    ["Engine Ready Wait", dumpPhases.engine_ready_wait_ms, "bg-slate-400"],
    ["Pre-Dump", dumpPhases.pre_dump_ms, "bg-amber-300"],
    ["Dump", dumpPhases.dump_ms, "bg-teal-500"],
    ["Post-Dump", dumpPhases.post_dump_ms, "bg-violet-400"],
  ];
  const phases: Phase[] = [];
  let cursor = 0;
  for (const [label, durationMs, color] of subPhaseSpecs) {
    if (durationMs == null || durationMs <= 0) continue;
    phases.push({ label, startMs: cursor, durationMs, color });
    cursor += durationMs;
  }
  return phases;
}

/**
 * Cryod timing waterfall — same visual structure as the CRIU TimingWaterfall
 * (cascading Gantt bars with expandable sub-phases) but with cryod-specific
 * phases: cold start, CRIU dump, mooncake transfer, CRIU restore (with
 * sub-phases when available), K8s overhead, HTTP ready, inference.
 */

/**
 * Build the source-side phase prefix shared by single-pod and multi-pod
 * paths: cold start (if non-fallback) → CRIU dump → mooncake transfer.
 * Returns the cumulative ms cursor where the restore phase should begin.
 */
function buildSourcePhasePrefix(result: CiTestResult, phases: Phase[]): number {
  let cursor = 0;
  const coldStartMs =
    result.cold_start_ms != null && result.cold_start_ms > 0 ? result.cold_start_ms : 0;
  const hasFallbackContext =
    (result.criu_dump_ms ?? 0) > 0 ||
    (result.mooncake_transfer_ms ?? 0) > 0 ||
    (result.transfer_seconds ?? 0) > 0 ||
    (result.criu_binary_duration_ms ?? 0) > 0 ||
    (result.criu_restore_ms ?? 0) > 0;
  const isFallbackColdStart = coldStartMs > 0 && hasFallbackContext;
  if (coldStartMs > 0 && !isFallbackColdStart) {
    phases.push({
      label: "Cold Start",
      startMs: cursor,
      durationMs: coldStartMs,
      color: "bg-slate-400",
    });
    cursor += coldStartMs;
  }
  if (result.criu_dump_ms != null && result.criu_dump_ms > 0) {
    const dumpChildren = result.dump_phases ? buildDumpSubPhases(result.dump_phases) : [];
    phases.push({
      label: "Checkpoint",
      startMs: cursor,
      durationMs: result.criu_dump_ms,
      color: "bg-teal-500",
      children: dumpChildren.length > 0 ? dumpChildren : undefined,
    });
    cursor += result.criu_dump_ms;
  }
  if (result.mooncake_transfer_ms != null && result.mooncake_transfer_ms > 0) {
    phases.push({
      label: "Mooncake Transfer",
      startMs: cursor,
      durationMs: result.mooncake_transfer_ms,
      color: "bg-blue-500",
    });
    cursor += result.mooncake_transfer_ms;
  } else if (result.transfer_seconds != null && result.transfer_seconds > 0) {
    const transferMs = msFromSeconds(result.transfer_seconds);
    phases.push({
      label: "Transfer",
      startMs: cursor,
      durationMs: transferMs,
      color: "bg-blue-500",
    });
    cursor += transferMs;
  }
  return cursor;
}

/**
 * Offsets (ms) of restore-side sub-windows inside the cryod agent's restore
 * window, derived from the agent-reported memory_phases epochs. All entries
 * come from the restore node's clock, so offsets between them are meaningful
 * even though the absolute values aren't comparable to bench timestamps.
 * Returns zeros when the data is missing — callers left-align inside the
 * window, which is the honest default.
 */
function restoreWindowOffsets(result: CiTestResult): {
  binaryOffsetMs: number;
  pullOffsetMs: number;
} {
  const memoryPhases = result.criu_phases?.memory_phases ?? [];
  const restoreWindowStart = memoryPhases.find(
    (memoryPhase) => memoryPhase.phase === "restore",
  )?.started_at;
  if (restoreWindowStart == null) return { binaryOffsetMs: 0, pullOffsetMs: 0 };
  const offsetOf = (phaseName: string): number => {
    const startedAt = memoryPhases.find(
      (memoryPhase) => memoryPhase.phase === phaseName,
    )?.started_at;
    if (startedAt == null) return 0;
    return Math.max(msFromSeconds(startedAt - restoreWindowStart), 0);
  };
  return { binaryOffsetMs: offsetOf("criu_restore"), pullOffsetMs: offsetOf("pull_checkpoint") };
}

/** Human labels for each storage-engine pull phase (keyed by pull_spans[].phase). */
const PULL_SPAN_LABELS: Record<string, string> = {
  checkpoint: "Checkpoint Pull",
  weights: "Weights Pull",
  "weights-gpu": "Weights Pull (GPU-direct)",
};

/** Restore-window start epoch (seconds) from the agent-reported memory_phases. */
function restoreWindowStartEpoch(result: CiTestResult): number | null {
  const memoryPhases = result.criu_phases?.memory_phases ?? [];
  return memoryPhases.find((memoryPhase) => memoryPhase.phase === "restore")?.started_at ?? null;
}

/**
 * One bar per storage-engine pull (checkpoint images, weights, weights-gpu),
 * each anchored at its true offset inside the restore window from the pull's
 * own started_at epoch — so the weights pull lands in the CRIU-binary tail
 * where it actually ran, instead of the summed mooncake_transfer_ms bar drawn
 * at the window front. Parent-relative offsets, clamped into the window.
 * Returns [] when pull_spans is absent (caller falls back to the single bar)
 * or when the restore-window epoch is missing (can't position honestly).
 */
function buildPullSpanPhases(result: CiTestResult, restoreWindowMs: number): Phase[] {
  const spans = result.pull_spans ?? [];
  if (spans.length === 0) return [];
  const windowStart = restoreWindowStartEpoch(result);
  if (windowStart == null) return [];
  return spans.map((span) => {
    const offsetMs = Math.min(
      Math.max(msFromSeconds(span.started_at - windowStart), 0),
      restoreWindowMs,
    );
    return {
      label: PULL_SPAN_LABELS[span.phase] ?? "Transfer Pull",
      startMs: offsetMs,
      durationMs: msFromSeconds(span.wall_sec),
      // Checkpoint-images pull is the small front bar; weights the big one.
      color: span.phase === "checkpoint" ? "bg-sky-400" : "bg-blue-500",
    };
  });
}

/**
 * Restore-window staging bar (untar/extract of the pulled images) anchored at
 * its true offset inside the window from staging_span.started_at. Fills the gap
 * between the checkpoint pull and the CRIU binary. Returns null when no
 * staging_span was parsed or the restore-window epoch is missing.
 */
function buildStagingPhase(result: CiTestResult, restoreWindowMs: number): Phase | null {
  const staging = result.staging_span;
  if (staging == null) return null;
  const windowStart = restoreWindowStartEpoch(result);
  if (windowStart == null) return null;
  const offsetMs = Math.min(
    Math.max(msFromSeconds(staging.started_at - windowStart), 0),
    restoreWindowMs,
  );
  return {
    label: staging.label || "Staging",
    startMs: offsetMs,
    durationMs: msFromSeconds(staging.wall_sec),
    color: "bg-amber-300",
  };
}

/** Cascading CRIU restore sub-phases for one pod's parsed restore.log. */
function buildPerPodRestoreSubPhases(criu_phases: RestorePodTiming["criu_phases"]): Phase[] {
  if (criu_phases == null) return [];
  const subPhases: Phase[] = [];
  let subCursor = 0;
  const subPhaseSpecs: [string, number, string][] = [
    ["Init + Image Read", msFromSeconds(criu_phases.init_s), "bg-green-300"],
    ["Process Fork", msFromSeconds(criu_phases.fork_s), "bg-green-400"],
    ["Resource Restore", msFromSeconds(criu_phases.resources_s), "bg-emerald-400"],
    ["Memory Remap", msFromSeconds(criu_phases.memory_remap_s), "bg-rose-400"],
    ["Network Unlock", msFromSeconds(criu_phases.network_unlock_s), "bg-purple-400"],
    ["Syscall Injection", msFromSeconds(criu_phases.syscall_inject_s), "bg-amber-400"],
    ["CUDA Restore", msFromSeconds(criu_phases.cuda_restore_s), "bg-orange-400"],
  ];
  for (const [label, duration, color] of subPhaseSpecs) {
    if (duration > 0) {
      subPhases.push({ label, startMs: subCursor, durationMs: duration, color });
      subCursor += duration;
    }
  }
  return subPhases;
}

/**
 * Build a multi-pod waterfall: source-side prefix once, then one row per
 * restore pod (label "Restore Pod N", 1-indexed for human readability —
 * the underlying ordinal is 0-based over the restore pods only) anchored
 * at the source-end cursor + the pod's apply-time offset relative to the
 * earliest pod.
 *
 * Per-pod row hierarchy (3 levels of nesting):
 *   Restore Pod N    — width = total restore (cryod agent annotation, or
 *                      bench wall-clock when annotation missing)
 *     CRIU Restore   — width = parsed restore.log total (CRIU binary time);
 *                      anchored at offset 0 inside the parent
 *       Init + Image Read / Process Fork / ... / CUDA Restore
 *
 * The outer Restore Pod row captures everything the cryod agent measured
 * (image pull, staging, NCCL helper, mooncake transfer, CRIU binary,
 * CUDA wake-up, network unlock); the CRIU Restore child is just the
 * binary portion, which sums cleanly to the sub-phase grandchildren.
 *
 * Pods that failed before they were applied carry zero-width bars; pods
 * that failed mid-restore are colored rose so they read as failed in the
 * shared waterfall.
 */
function buildMultiPodPhases(
  result: CiTestResult,
  restorePods: RestorePodTiming[],
): { phases: Phase[]; totalMs: number; failedOrdinals: Set<number> } {
  const phases: Phase[] = [];
  const sourceCursor = buildSourcePhasePrefix(result, phases);

  const earliestStart = restorePods.reduce((min, pod) => {
    const ms = new Date(pod.started_at).getTime();
    return ms < min ? ms : min;
  }, Number.POSITIVE_INFINITY);
  const failedOrdinals = new Set<number>();

  let maxRestoreEnd = sourceCursor;
  for (const pod of restorePods) {
    const offsetMs = Math.max(new Date(pod.started_at).getTime() - earliestStart, 0);
    // Outer bar = cryod agent total (the cryod/restore-duration-ms
    // annotation). Falls back to bench wall-clock, then CRIU binary as
    // a last resort. This is the "full restore" the operator sees.
    const totalRestoreMs =
      pod.criu_restore_duration_ms != null && pod.criu_restore_duration_ms > 0
        ? pod.criu_restore_duration_ms
        : pod.restore_duration_ms > 0
          ? pod.restore_duration_ms
          : (pod.criu_binary_duration_ms ?? 0);
    // Inner CRIU Restore child = parsed restore.log total. Sums to the
    // grandchild phase bars exactly so the visual hierarchy stays tight.
    const criuBinaryMs =
      pod.criu_binary_duration_ms != null && pod.criu_binary_duration_ms > 0
        ? pod.criu_binary_duration_ms
        : null;
    const startMs = sourceCursor + offsetMs;
    const isFailed = pod.status === "fail";
    if (isFailed) failedOrdinals.add(pod.ordinal);

    // Build the inner CRIU Restore phase + its grandchild sub-phases.
    // Anchored at offset 0 within the parent restore row: bench doesn't
    // know precisely when CRIU started inside the agent's window (image
    // pull/staging happen first), so left-aligning is the most honest
    // choice.
    const criuPhaseChildren = buildPerPodRestoreSubPhases(pod.criu_phases);
    const innerPhases: Phase[] = [];
    if (criuBinaryMs != null) {
      innerPhases.push({
        label: "CRIU Restore",
        startMs: 0,
        durationMs: criuBinaryMs,
        color: "bg-green-400",
        children: criuPhaseChildren.length > 0 ? criuPhaseChildren : undefined,
        tooltip:
          "Time CRIU's restore binary spent (parsed from restore.log). " +
          "The gap between this and the parent Restore Pod row is non-CRIU " +
          "agent work: image pull, staging, NCCL helper setup, CUDA " +
          "wake-up, network unlock.",
      });
    }

    const tooltipLines = [
      `pod_uid: ${pod.pod_uid || "(unknown)"}`,
      `node: ${pod.node_name || "(unknown)"}`,
      pod.criu_restore_duration_ms != null
        ? `cryod agent total (annotation): ${pod.criu_restore_duration_ms}ms`
        : null,
      pod.criu_binary_duration_ms != null
        ? `CRIU binary (parsed log): ${pod.criu_binary_duration_ms}ms`
        : null,
      `wall-clock (apply→Ready): ${pod.restore_duration_ms}ms`,
      pod.error ? `error: ${pod.error}` : `status: ${pod.status}`,
    ].filter((line): line is string => line != null);
    phases.push({
      label: `Restore Pod ${pod.ordinal + 1}`,
      startMs,
      durationMs: totalRestoreMs,
      // Fail pods get a rose bar; pass pods use a darker green so the
      // CRIU Restore child (lighter green) reads as nested.
      color: isFailed ? "bg-rose-500" : "bg-green-600",
      children: innerPhases.length > 0 ? innerPhases : undefined,
      tooltip: tooltipLines.join("\n"),
    });
    maxRestoreEnd = Math.max(maxRestoreEnd, startMs + totalRestoreMs);
  }

  // Tail phases (HTTP Ready, Inference) drawn relative to the latest pod end.
  let tailCursor = maxRestoreEnd;
  if (result.http_ready_ms != null && result.http_ready_ms > 0) {
    phases.push({
      label: "HTTP Ready",
      startMs: tailCursor,
      durationMs: result.http_ready_ms,
      color: "bg-amber-500",
    });
    tailCursor += result.http_ready_ms;
  }
  if (result.inference_ms != null && result.inference_ms > 0) {
    phases.push({
      label: "Inference",
      startMs: tailCursor,
      durationMs: result.inference_ms,
      color: "bg-rose-400",
    });
    tailCursor += result.inference_ms;
  }

  // Recurse so grandchildren (CRIU phase rows under rst-N → CRIU Restore)
  // also get tooltips. Don't clobber tooltips we set explicitly above.
  const attachTooltips = (phase: Phase) => {
    if (phase.tooltip == null) phase.tooltip = getPhaseTooltip(phase.label);
    if (phase.children) {
      for (const child of phase.children) {
        attachTooltips(child);
      }
    }
  };
  for (const phase of phases) attachTooltips(phase);

  return { phases, totalMs: tailCursor, failedOrdinals };
}

export function buildPhases(result: CiTestResult): { phases: Phase[]; totalMs: number } {
  const phases: Phase[] = [];
  let cursor = 0;

  // cold_start_ms is overloaded:
  //   1) "pure cold start" rows — there is no CRIU checkpoint to dump and no
  //      restore was attempted; cold_start_ms is the only timing in the row
  //      and renders at the front.
  //   2) "CRIU restore failed → cold-start fallback" rows (test_runner.py:2806;
  //      `restore_method == "cold-start"`) — cold_start_ms is the destination-
  //      host fresh boot wall clock that runs *after* the failed CRIU restore
  //      attempt. The real sequence is:
  //         dump → transfer → CRIU Binary (failed) → cold-start fallback
  //      so we have to defer the bar until after CRIU Binary / CRIU Restore
  //      and label it "Cold Start (Fallback)" so the tooltip can disambiguate.
  //
  // Heuristic: if cold_start_ms is set alongside ANY source-side or transfer
  // timing, it must be a fallback row — the source pod's own cold-start lives
  // inside criu_dump_ms's engine_ready_wait_ms sub-bar, never in cold_start_ms.
  const coldStartMs =
    result.cold_start_ms != null && result.cold_start_ms > 0 ? result.cold_start_ms : 0;
  const hasFallbackContext =
    (result.criu_dump_ms ?? 0) > 0 ||
    (result.mooncake_transfer_ms ?? 0) > 0 ||
    (result.transfer_seconds ?? 0) > 0 ||
    (result.criu_binary_duration_ms ?? 0) > 0 ||
    (result.criu_restore_ms ?? 0) > 0;
  const isFallbackColdStart = coldStartMs > 0 && hasFallbackContext;

  if (coldStartMs > 0 && !isFallbackColdStart) {
    phases.push({
      label: "Cold Start",
      startMs: cursor,
      durationMs: coldStartMs,
      color: "bg-slate-400",
    });
    cursor += coldStartMs;
  }

  if (result.criu_dump_ms != null && result.criu_dump_ms > 0) {
    const dumpChildren = result.dump_phases ? buildDumpSubPhases(result.dump_phases) : [];
    phases.push({
      label: "Checkpoint",
      startMs: cursor,
      durationMs: result.criu_dump_ms,
      color: "bg-teal-500",
      children: dumpChildren.length > 0 ? dumpChildren : undefined,
    });
    cursor += result.criu_dump_ms;
  }

  // --- Restore section ---------------------------------------------------
  // The cryod agent's restore window (criu_restore_ms) CONTAINS the
  // storage-engine pull, the CRIU binary, and the engine wake — with
  // GPU-direct pull the transfer runs concurrently with the CRIU binary.
  // When the agent window is known, render those as overlapping spans
  // positioned inside a "Restore" container row (offsets from the
  // agent-reported memory_phases epochs, left-aligned when absent) instead
  // of serializing them. Failed-restore fallback rows keep the serialized
  // layout: the attempt aborted, so overlap placement and a wake bar would
  // be fiction.
  const transferMs =
    result.mooncake_transfer_ms != null && result.mooncake_transfer_ms > 0
      ? result.mooncake_transfer_ms
      : result.transfer_seconds != null && result.transfer_seconds > 0
        ? msFromSeconds(result.transfer_seconds)
        : null;
  const transferLabel =
    result.mooncake_transfer_ms != null && result.mooncake_transfer_ms > 0
      ? "Mooncake Transfer"
      : "Transfer";
  const criuBinaryMs =
    result.criu_binary_duration_ms != null && result.criu_binary_duration_ms > 0
      ? result.criu_binary_duration_ms
      : null;
  const restoreWindowMs =
    result.criu_restore_ms != null && result.criu_restore_ms > 0 ? result.criu_restore_ms : null;
  const subPhases = buildPerPodRestoreSubPhases(result.criu_phases);

  if (!isFallbackColdStart && restoreWindowMs != null) {
    const { binaryOffsetMs, pullOffsetMs } = restoreWindowOffsets(result);
    // Children are parent-relative: offsets are within the agent window.
    const restoreChildren: Phase[] = [];
    // Prefer per-pull spans (each at its true start) over the single summed
    // Mooncake Transfer bar. The cryod agent does two pulls — checkpoint images
    // then weights — at different times; pull_spans splits them so the weights
    // pull renders in the CRIU-binary tail where it ran, not fused at t=0.
    const pullSpanPhases = buildPullSpanPhases(result, restoreWindowMs);
    if (pullSpanPhases.length > 0) {
      restoreChildren.push(...pullSpanPhases);
    } else if (transferMs != null) {
      restoreChildren.push({
        label: transferLabel,
        startMs: Math.min(pullOffsetMs, restoreWindowMs),
        durationMs: transferMs,
        color: "bg-blue-500",
      });
    }
    // Staging (untar/extract) fills the gap between the checkpoint pull and the
    // CRIU binary — rendered as its own bar when the agent log yields it.
    const stagingPhase = buildStagingPhase(result, restoreWindowMs);
    if (stagingPhase != null) {
      restoreChildren.push(stagingPhase);
    }
    if (criuBinaryMs != null) {
      const binaryStartMs = Math.min(binaryOffsetMs, Math.max(restoreWindowMs - criuBinaryMs, 0));
      restoreChildren.push({
        label: "CRIU Binary",
        startMs: binaryStartMs,
        durationMs: criuBinaryMs,
        color: "bg-green-500",
        children: subPhases.length > 0 ? subPhases : undefined,
      });
      // The remainder of the agent window after the CRIU binary exits:
      // engine wake (H2D weights from the sleep arena) + NCCL helper +
      // network unlock. A true span now, not a duration subtraction — the
      // concurrent transfer no longer eats it.
      const wakeMs = restoreWindowMs - (binaryStartMs + criuBinaryMs);
      if (wakeMs > 0) {
        restoreChildren.push({
          label: "Wake + Overhead",
          startMs: binaryStartMs + criuBinaryMs,
          durationMs: wakeMs,
          color: "bg-cyan-500",
        });
      }
    }
    phases.push({
      label: "Restore",
      startMs: cursor,
      durationMs: restoreWindowMs,
      color: "bg-green-600",
      // Without a parsed CRIU binary duration there is no CRIU Binary row,
      // so the restore.log sub-phases hang off the window itself (collapsed,
      // like the old CRIU Restore row). The transfer/binary/wake breakdown
      // expands by default — it IS the restore story.
      children:
        restoreChildren.length > 0 ? restoreChildren : subPhases.length > 0 ? subPhases : undefined,
      defaultExpanded: restoreChildren.length > 0,
    });
    cursor += restoreWindowMs;
  } else {
    // Serialized fallback: no agent restore window (or a failed attempt).
    if (transferMs != null) {
      phases.push({
        label: transferLabel,
        startMs: cursor,
        durationMs: transferMs,
        color: "bg-blue-500",
      });
      cursor += transferMs;
    }
    const serializedRestoreMs = criuBinaryMs ?? restoreWindowMs;
    if (serializedRestoreMs != null) {
      phases.push({
        label: criuBinaryMs != null ? "CRIU Binary" : "CRIU Restore",
        startMs: cursor,
        durationMs: serializedRestoreMs,
        color: "bg-green-500",
        children: subPhases.length > 0 ? subPhases : undefined,
      });
      cursor += serializedRestoreMs;
    }
  }

  // Fallback cold-start bar — runs immediately after the failed CRIU Binary /
  // CRIU Restore attempt completes. See the comment at the top of buildPhases
  // for why this is rendered here instead of at the front.
  if (isFallbackColdStart) {
    phases.push({
      label: "Cold Start (Fallback)",
      startMs: cursor,
      durationMs: coldStartMs,
      color: "bg-slate-400",
    });
    cursor += coldStartMs;
  }

  if (result.kubernetes_overhead_ms != null && result.kubernetes_overhead_ms > 0) {
    phases.push({
      label: "K8s Overhead",
      startMs: cursor,
      durationMs: result.kubernetes_overhead_ms,
      color: "bg-violet-500",
    });
    cursor += result.kubernetes_overhead_ms;
  }

  if (result.http_ready_ms != null && result.http_ready_ms > 0) {
    phases.push({
      label: "HTTP Ready",
      startMs: cursor,
      durationMs: result.http_ready_ms,
      color: "bg-amber-500",
    });
    cursor += result.http_ready_ms;
  }

  if (result.inference_ms != null && result.inference_ms > 0) {
    phases.push({
      label: "Inference",
      startMs: cursor,
      durationMs: result.inference_ms,
      color: "bg-rose-400",
    });
    cursor += result.inference_ms;
  }

  // Attach tooltips from the shared description map (case-insensitive lookup).
  // Same pattern as TimingWaterfall — phases without a tooltip render with the
  // plain label, phases that do match get the dotted-underline + hover help.
  const attachTooltips = (phase: Phase) => {
    phase.tooltip = getPhaseTooltip(phase.label);
    for (const child of phase.children ?? []) {
      attachTooltips(child);
    }
  };
  for (const phase of phases) attachTooltips(phase);

  // Spans overlap now, so the domain is the furthest bar end (including
  // nested children), not a running cursor sum — a transfer bar from a
  // pre-hash-filter run can even stick out past the restore window; let it,
  // rather than hiding the inconsistency.
  const totalMs = phases.reduce((maxEnd, phase) => Math.max(maxEnd, deepEndMs(phase, 0)), cursor);
  return { phases, totalMs };
}

/** Furthest absolute end (ms) of a phase or any of its (parent-relative) descendants. */
function deepEndMs(phase: Phase, offsetMs: number): number {
  const startMs = offsetMs + phase.startMs;
  let maxEnd = startMs + phase.durationMs;
  for (const child of phase.children ?? []) {
    maxEnd = Math.max(maxEnd, deepEndMs(child, startMs));
  }
  return maxEnd;
}

/**
 * Maps backend failure_phase enum to the candidate labels that buildPhases may emit.
 * Some phases have two possible labels depending on which fields are populated
 * (e.g. "Transfer" when only transfer_seconds is set vs "Mooncake Transfer").
 */
const failurePhaseToLabels: Record<string, readonly string[]> = {
  start: ["Cold Start", "Cold Start (Fallback)"],
  dump: ["Checkpoint"],
  transfer: ["Mooncake Transfer", "Transfer"],
  restore: ["CRIU Binary", "CRIU Restore", "Restore", "Cold Start (Fallback)"],
  wake_up: ["Wake + Overhead", "CRIU Binary", "CRIU Restore", "Restore", "Cold Start (Fallback)"],
  http_ready: ["Inference"],
  inference: ["Inference"],
  k8s: ["K8s Overhead"],
  machine: [],
};

function resolveFailedLabel(
  failurePhase: string | null | undefined,
  phases: Phase[],
): string | null {
  if (!failurePhase) return null;
  const candidates = failurePhaseToLabels[failurePhase];
  if (!candidates) return null;
  // Candidate labels can be nested (transfer/binary/wake live under the
  // Restore row), so collect labels from the whole tree.
  const phaseLabels = new Set<string>();
  const collectLabels = (phase: Phase) => {
    phaseLabels.add(phase.label);
    for (const child of phase.children ?? []) {
      collectLabels(child);
    }
  };
  for (const phase of phases) collectLabels(phase);
  for (const candidate of candidates) {
    if (phaseLabels.has(candidate)) return candidate;
  }
  return null;
}

export function CryodTimingWaterfall({
  result,
  failurePhase,
}: {
  result: CiTestResult;
  failurePhase?: string | null;
}) {
  const restorePods = result.restore_pods ?? [];
  // Two or more pods unlock the multi-pod waterfall: source-side prefix
  // once, then one rst-N row per pod anchored at apply-time offset.
  // Single-pod tests fall through to the original buildPhases path so
  // their CRIU Restore row keeps the existing sub-phase expansion.
  if (restorePods.length >= 2) {
    const sortedPods = [...restorePods].sort((podA, podB) => podA.ordinal - podB.ordinal);
    const { phases, totalMs, failedOrdinals } = buildMultiPodPhases(result, sortedPods);
    // Highlight every failed pod row when the test failed in restore/inference;
    // resolveFailedLabel only returns one label, so we handle multi-pod
    // failures here by passing the first failed pod's row label (the rest
    // pick up their per-bar rose color from buildMultiPodPhases).
    const firstFailedOrdinal = failedOrdinals.size > 0 ? Math.min(...failedOrdinals) : null;
    const failedLabel = firstFailedOrdinal != null ? `Restore Pod ${firstFailedOrdinal + 1}` : null;
    return (
      <WaterfallShell
        phases={phases}
        totalMs={totalMs}
        failedLabel={failedLabel}
        totalRestoreMs={result.total_restore_ms}
      />
    );
  }

  const { phases, totalMs } = buildPhases(result);
  const failedLabel = resolveFailedLabel(failurePhase, phases);

  return (
    <WaterfallShell
      phases={phases}
      totalMs={totalMs}
      failedLabel={failedLabel}
      totalRestoreMs={result.total_restore_ms}
    />
  );
}
